import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import Axios from "@/lib/axios-instance";

// Define the response type
interface CancelSubscriptionResponse {
  meta: {
    error: boolean;
    code: number;
    message: string;
  };
  data: {
    subscription_id: string;
    store_id: string;
    plan_id: number;
    plan_name: string;
    plan_price: number;
    billing_interval: string;
    cancellation_date: string;
    end_date: string;
  } | null; // Allow data to be null for error responses
}

interface CancelSubscriptionParams {
  store_id: string;
}

export const cancelSubscription = async ({ store_id }: CancelSubscriptionParams): Promise<CancelSubscriptionResponse> => {
  try {
    const response = await Axios.post(`/customer-subscription/cancel?store_id=${store_id}`);
    return response.data;
  } catch (error: any) {
    if (error.response) {
      // Return the error response to handle it properly in the UI
      return error.response.data;
    }
    throw new Error(error.message || "Network Error");
  }
};

export const useCancelSubscription = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: cancelSubscription,
    onSuccess: (response, variables) => {
      // Check if there's an error in the response
      if (response.meta.error) {
        toast.error("Subscription Cancellation Error", {
          description: response.meta.message || "An error occurred while canceling subscription"
        });
      } else {
        // Show success message
        toast.success("Subscription Canceled", {
          description: response.meta.message || "Your subscription has been successfully canceled."
        });
        
        // Invalidate the specific subscription query to refresh the data
        queryClient.invalidateQueries({ 
          queryKey: ["get-my-subscription", variables.store_id] 
        });
      }
    },
    onError: (error: any) => {
      console.error("Error canceling subscription:", error);
      toast.error("Error", {
        description: error.message || "An error occurred while processing your subscription cancellation"
      });
    },
  });
};